import React, { useState, useEffect, useRef } from 'react';
import { Link } from '@inertiajs/react';
import {
  Container,
  Tabs,
  Title,
  Card,
  Text,
  Switch,
  Button,
  Group,
  Badge,
  Box,
  Stack,
  Drawer,
  TextInput,
  NumberInput,
  Checkbox,
  ColorInput,
  Modal,
  Paper,
  Divider,
  ActionIcon,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import {
  IconCalendar,
  IconClock,
  IconUser,
  IconTrash,
  IconEye,
  IconMapPin,
  IconTag,
  IconCoin,
  IconCheck,
  IconCalendarTime,
  IconNotes,
} from '@tabler/icons-react';

interface PrivateLessonType {
  id: number;
  name: string;
  location: string;
  duration: number;
  price: number;
  payment_required: boolean;
  color: string;
}

interface PrivateLessonPage {
  id: number;
  description: string;
  additional_info: string;
  timezone: string;
  slug: string;
  created_at: string;
  private_lesson_type: {
    id: number;
    name: string;
    location: string;
    duration: number;
    price: number;
    color: string;
  };
}

interface TimeSlot {
  id: number;
  start_time: string;
  end_time: string;
}

interface PrivateLessonBooking {
  id: number;
  private_lesson_page_id: number;
  private_lesson_page: {
    id: number;
    description: string;
    additional_info: string;
    timezone: string;
    slug: string;
    event_id?: number;
    private_lesson_type: {
      id: number;
      name: string;
      location: string;
      duration: number;
      price: number;
      color: string;
    };
  };
  user_id: number;
  user?: {
    id: number;
    username: string;
  };
  time_slot_id: number;
  notes: string;
  video_recap_url: string | null;
  survey_url: string | null;
  created_at: string;
  updated_at: string;
  status: number;
  time_slot: TimeSlot;
}

interface Artist {
  id: number;
  name: string;
  slug: string;
  private_lessons_enabled: boolean;
}

interface PrivateLessonsManagementProps {
  currentUser: {
    id: number;
    username: string;
  };
  claimedArtist: {
    id: number;
    name: string;
    slug: string;
    private_lessons_enabled: boolean;
  };
  privateLessonTypes?: PrivateLessonType[];
  privateLessonPages?: PrivateLessonPage[];
  privateLessonBookings?: PrivateLessonBooking[];
  initialActiveTab?: string | null;
}

const PrivateLessonsManagement = ({
  currentUser,
  claimedArtist,
  privateLessonTypes = [],
  privateLessonPages = [],
  privateLessonBookings = [],
  initialActiveTab = null,
}: PrivateLessonsManagementProps) => {
  // State
  const [activeTab, setActiveTab] = useState<string | null>('pl-enable');
  const [lessonsEnabled, setLessonsEnabled] = useState(claimedArtist.private_lessons_enabled);
  const [lessonTypes, setLessonTypes] = useState<PrivateLessonType[]>([]);
  const [lessonPages, setLessonPages] = useState<PrivateLessonPage[]>([]);
  const [lessonBookings, setLessonBookings] = useState<PrivateLessonBooking[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<'new-type' | 'edit-type' | 'new-page'>('new-type');
  const [drawerTitle, setDrawerTitle] = useState('');
  const [selectedLessonType, setSelectedLessonType] = useState<PrivateLessonType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [bookingToDecline, setBookingToDecline] = useState<number | null>(null);
  const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
  const [bookingToApprove, setBookingToApprove] = useState<number | null>(null);
  const [isRecapModalOpen, setIsRecapModalOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<number | null>(null);
  const [recapText, setRecapText] = useState('');

  useEffect(() => {
    if (initialActiveTab) {
      console.log('PrivateLessonsManagement initialized with tab:', initialActiveTab);
    }
  }, []);

  useEffect(() => {
    if (initialActiveTab && initialActiveTab !== activeTab) {
      console.log('Updating active tab to:', initialActiveTab);
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab]);

  // Use direct initialization during first render
  useEffect(() => {
    console.log('Setting initial state from props');
    setLessonTypes(privateLessonTypes || []);
    setLessonPages(privateLessonPages || []);
    setLessonBookings(privateLessonBookings || []);
  }, []); // Empty dependency array for first render only

  // Update when props change
  useEffect(() => {
    console.log('Props changed, updating state:');

    if (privateLessonTypes && privateLessonTypes.length > 0) {
      setLessonTypes(privateLessonTypes);
    }

    if (privateLessonPages && privateLessonPages.length > 0) {
      setLessonPages(privateLessonPages);
    }

    if (privateLessonBookings && privateLessonBookings.length > 0) {
      setLessonBookings(privateLessonBookings);
    }
  }, [privateLessonTypes, privateLessonPages, privateLessonBookings]);

  const handleApproveBooking = () => {
    if (!bookingToApprove) {
      console.error('No booking selected for approval');
      return;
    }

    setIsLoading(true);
    console.log('Approving booking with ID:', bookingToApprove);

    const booking = lessonBookings.find((b) => b.id === bookingToApprove);
    if (!booking) {
      console.error('Booking not found');
      setIsLoading(false);
      setIsApproveModalOpen(false);
      return;
    }

    const artistSlug = getArtistSlug(booking.private_lesson_page_id);
    const pageSlug = getCorrectPageSlug(bookingToApprove);
    if (!pageSlug) {
      console.error('Could not find page slug for booking');
      setIsLoading(false);
      setIsApproveModalOpen(false);
      return;
    }

    fetch(
      `/users/${currentUser.username}/${artistSlug}/pl/${pageSlug}/private_lesson_bookings/${bookingToApprove}/approve`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          Accept: 'application/json',
        },
      },
    )
      .then((response) => {
        if (response.redirected) {
          window.location.href = response.url;
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to approve booking');
        }

        return response.json().catch(() => {
          return { success: true };
        });
      })
      .then((data) => {
        if (data) {
          // Update the booking status in state
          setLessonBookings(
            lessonBookings.map((booking) =>
              booking.id === bookingToApprove
                ? { ...booking, status: 1 } // Set to Approved status
                : booking,
            ),
          );
          setIsLoading(false);
          setIsApproveModalOpen(false);
          setBookingToApprove(null);
        }
      })
      .catch((error) => {
        console.error('Error approving booking:', error);
        setIsLoading(false);
        setIsApproveModalOpen(false);
        setBookingToApprove(null);
      });
  };

  // Function to handle marking a booking as completed
  const handleMarkCompleted = (bookingId: number) => {
    setIsLoading(true);

    fetch(`/users/${currentUser.username}/complete_booking/${bookingId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to mark booking as completed');
        }
        return response.json();
      })
      .then((data) => {
        // Update the booking status in state
        setLessonBookings(
          lessonBookings.map((booking) =>
            booking.id === bookingId
              ? { ...booking, status: 2 } // Set to Completed status
              : booking,
          ),
        );
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error completing booking:', error);
        setIsLoading(false);
      });
  };

  // Function to open the recap modal
  const openRecapModal = (bookingId: number) => {
    const booking = lessonBookings.find((b) => b.id === bookingId);
    setSelectedBookingId(bookingId);
    setRecapText(booking?.video_recap_url || '');
    setIsRecapModalOpen(true);
  };

  // Function to save the recap
  const handleSaveRecap = () => {
    if (!selectedBookingId) return;

    setIsLoading(true);

    fetch(`/users/${currentUser.username}/add_recap/${selectedBookingId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        video_recap_url: recapText,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to save recap');
        }
        return response.json();
      })
      .then((data) => {
        // Update the booking in state
        setLessonBookings(
          lessonBookings.map((booking) =>
            booking.id === selectedBookingId ? { ...booking, video_recap_url: recapText } : booking,
          ),
        );
        setIsLoading(false);
        setIsRecapModalOpen(false);
        setSelectedBookingId(null);
        setRecapText('');
      })
      .catch((error) => {
        console.error('Error saving recap:', error);
        setIsLoading(false);
      });
  };

  const handleToggleLessons = () => {
    setIsLoading(true);

    // Make a real API call to update the artist's private_lessons_enabled status
    fetch(`/users/${currentUser.username}/toggle_private_lessons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        enabled: !lessonsEnabled,
      }),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to update private lessons setting');
        }
        return response.json();
      })
      .then((data) => {
        setLessonsEnabled(!lessonsEnabled);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error updating private lessons setting:', error);
        setIsLoading(false);
      });
  };

  const openDrawer = (type: 'new-type' | 'edit-type' | 'new-page', item?: PrivateLessonType) => {
    setDrawerType(type);

    if (type === 'new-type') {
      setDrawerTitle('Create New Private Lesson Type');
      setSelectedLessonType(null);
    } else if (type === 'edit-type' && item) {
      setDrawerTitle(`Edit Private Lesson Type: ${item.name}`);
      setSelectedLessonType(item);
    } else if (type === 'new-page') {
      setDrawerTitle('Create New Private Lesson Page');
    }

    setIsDrawerOpen(true);
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    // Get date components in UTC
    const month = date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
    const day = date.getUTCDate();
    const year = date.getUTCFullYear();

    // Get time components in UTC (reusing same logic as getUTCTime)
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;

    return `${month} ${day}, ${year} at ${hours12}:${minutesFormatted} ${ampm}`;
  };

  const getUTCTime = (dateString) => {
    const date = new Date(dateString);
    const hours = date.getUTCHours();
    const minutes = date.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert to 12-hour format (0 becomes 12)

    // Add leading zeros to minutes if needed
    const minutesFormatted = minutes < 10 ? `0${minutes}` : minutes;

    return `${hours12}:${minutesFormatted} ${ampm}`;
  };

  // Get booking status text
  const getStatusText = (status: number) => {
    const STATUSES = ['Pending', 'Approved', 'Declined', 'Completed'];
    return STATUSES[status] || 'Unknown';
  };

  // Get status color
  const getStatusColor = (status: number) => {
    switch (status) {
      case 0: // Pending
        return 'yellow';
      case 1: // Approved
        return 'blue';
      case 2: // Declined (not 3)
        return 'red';
      case 3: // Completed (not 2)
        return 'green';
      default:
        return 'gray';
    }
  };

  // Helper functions to get information from the page
  const getPageName = (pageId: number) => {
    const page = lessonPages.find((p) => p.id === pageId);
    return page ? page.description : 'Unknown Page';
  };

  // Add this function to fetch student names
  const getStudentName = (bookingId) => {
    const booking = lessonBookings.find((b) => b.id === bookingId);

    if (booking && booking.user) {
      return booking.user.username;
    }

    return 'Unknown Student';
  };

  const getLessonTypeName = (bookingId: number) => {
    const booking = lessonBookings.find((b) => b.id === bookingId);

    if (booking && booking.private_lesson_page && booking.private_lesson_page.private_lesson_type) {
      return booking.private_lesson_page.private_lesson_type.name;
    }

    return 'Unknown Type';
  };

  const getLessonTypePrice = (bookingId: number) => {
    const booking = lessonBookings.find((b) => b.id === bookingId);

    if (booking && booking.private_lesson_page && booking.private_lesson_page.private_lesson_type) {
      return booking.private_lesson_page.private_lesson_type.price;
    }

    return 0;
  };

  const getEventName = (pageId: number) => {
    // Since we don't have event info in our current structure, return null
    return null;
  };

  const getArtistSlug = (pageId: number) => {
    // Use the claimed artist slug
    return claimedArtist.slug;
  };

  const getCorrectPageSlug = (bookingId: number) => {
    console.log(`Looking for page slug for booking ID: ${bookingId}`);

    // Find the booking by ID
    const booking = lessonBookings.find((b) => b.id === bookingId);
    console.log('Full booking object:', booking);
    console.log('Booking keys:', booking ? Object.keys(booking) : null);

    if (!booking) return null;

    // Try accessing private_lesson_page_id directly
    let pageId = booking.private_lesson_page_id;

    // If not available, maybe it's nested differently
    if (booking.private_lesson_page) {
      console.log('Booking has private_lesson_page object:', booking.private_lesson_page);
      pageId = booking.private_lesson_page.id;
    }

    console.log(`Looking for page with ID: ${pageId}`);

    // Find the page in the lessonPages array
    const page = lessonPages.find((p) => p.id === pageId);

    if (!page) {
      console.error(`No page found with ID: ${pageId}`);
      // As a fallback, try to use the second page which seems to be the right one
      const fallbackPage = lessonPages.find((p) => p.id === 2);
      if (fallbackPage) {
        console.log('Using fallback page with ID 2:', fallbackPage);
        return fallbackPage.slug;
      }
      return null;
    }

    return page.slug;
  };

  const getPageSlug = (bookingId: number) => {
    // Find the booking
    const booking = lessonBookings.find((b) => b.id === bookingId);

    // If booking has a direct reference to private_lesson_page with slug
    if (booking && booking.private_lesson_page && booking.private_lesson_page.slug) {
      return booking.private_lesson_page.slug;
    }

    // Otherwise, try to find the page by ID
    if (booking) {
      const page = lessonPages.find((p) => p.id === booking.private_lesson_page_id);
      return page?.slug || 'unknown-page';
    }

    return 'unknown-page';
  };

  // Open delete confirmation modal
  const openDeclineModal = (bookingId: number) => {
    setBookingToDecline(bookingId);
    setIsDeclineModalOpen(true); // You might also want to rename this state variable
  };

  const openApproveModal = (bookingId: number) => {
    setBookingToApprove(bookingId);
    setIsApproveModalOpen(true);
  };

  // Handle booking deletion
  const handleDeclineBooking = () => {
    if (!bookingToDecline) {
      console.error('No booking selected for decline');
      return;
    }

    setIsLoading(true);
    console.log('Declining booking with ID:', bookingToDecline);

    const booking = lessonBookings.find((b) => b.id === bookingToDecline);
    if (!booking) {
      console.error('Booking not found');
      setIsLoading(false);
      setIsDeclineModalOpen(false);
      return;
    }

    const artistSlug = getArtistSlug(booking.private_lesson_page_id);
    const pageSlug = getCorrectPageSlug(bookingToDecline);
    if (!pageSlug) {
      console.error('Could not find page slug for booking');
      setIsLoading(false);
      setIsDeclineModalOpen(false);
      return;
    }

    // Use the full route to match your controller action
    fetch(
      `/users/${currentUser.username}/${artistSlug}/pl/${pageSlug}/private_lesson_bookings/${bookingToDecline}/decline`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
          // Add Accept header to indicate we prefer JSON
          Accept: 'application/json',
        },
      },
    )
      .then((response) => {
        if (response.redirected) {
          // Handle redirect from the server
          window.location.href = response.url;
          return;
        }

        if (!response.ok) {
          throw new Error('Failed to decline booking');
        }

        // If we get here, we can handle as JSON
        return response.json().catch(() => {
          // If parsing JSON fails, just return success
          return { success: true };
        });
      })
      .then((data) => {
        // Only update the state if we're not being redirected
        if (data) {
          // Update the booking status in state
          setLessonBookings(
            lessonBookings.map((booking) =>
              booking.id === bookingToDecline
                ? { ...booking, status: 2 } // Set to Declined status
                : booking,
            ),
          );
          setIsLoading(false);
          setIsDeclineModalOpen(false);
          setBookingToDecline(null);
        }
      })
      .catch((error) => {
        console.error('Error declining booking:', error);
        setIsLoading(false);
        setIsDeclineModalOpen(false);
        setBookingToDecline(null);
      });
  };

  // Render the dynamic drawer content based on type
  const renderDrawerContent = () => {
    if (drawerType === 'new-type' || drawerType === 'edit-type') {
      return (
        <Stack gap="md" p="md">
          <TextInput
            label="Name"
            placeholder="e.g. One-on-One Coaching"
            defaultValue={selectedLessonType?.name || ''}
            required
          />
          <TextInput
            label="Location"
            placeholder="e.g. Online via Zoom or Studio 42"
            style={{ color: '#f2f2f2' }}
            defaultValue={selectedLessonType?.location || ''}
            required
          />
          <ColorInput label="Color" defaultValue={selectedLessonType?.color || '#000000'} />
          <NumberInput label="Duration (minutes)" min={1} defaultValue={selectedLessonType?.duration || 60} required />
          <Checkbox label="Payment Required" defaultChecked={selectedLessonType?.payment_required || false} />
          <NumberInput label="Price ($)" min={0} defaultValue={selectedLessonType?.price || 0} required />
          <Button fullWidth mt="xl" size="lg" color="orange">
            {drawerType === 'new-type' ? 'Create Private Lesson Type' : 'Update Private Lesson Type'}
          </Button>
        </Stack>
      );
    } else if (drawerType === 'new-page') {
      // Form for creating a lesson page
      return (
        <Stack gap="md" p="md">
          {/* We would need to select a lesson type here */}
          <Text fw={500} mb="xs">
            Select a Lesson Type
          </Text>
          {/* This would be a select dropdown in a real implementation */}
          <Text mb="md">We would have a dropdown of lesson types here</Text>

          <TextInput label="Description" placeholder="e.g. Beginner Dance Lessons" required />
          <TextInput label="Additional Info" placeholder="e.g. No experience required" />
          <TextInput label="Timezone" placeholder="e.g. America/New_York" required />

          <Button fullWidth mt="xl" size="lg" color="orange">
            Create Private Lesson Page
          </Button>
        </Stack>
      );
    }

    return null;
  };

  // CSS styles for modern card layout
  const modernCardStyles = {
    bookingsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '20px',
      marginBottom: '30px',
    },
    bookingCard: {
      backgroundColor: '#757575',
      borderRadius: '10px',
      overflow: 'hidden',
      transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3)',
      },
    },
    cardHeader: {
      backgroundColor: '#3a3a3a',
      padding: '15px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    },
    cardBody: {
      padding: '16px',
    },
    cardFooter: {
      padding: '15px',
      borderTop: '1px solid rgba(255, 255, 255, 0.1)',
      backgroundColor: '#3a3a3a',
    },
    infoRow: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '0px',
    },
    iconWrapper: {
      width: '24px',
      height: '24px',
      marginRight: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    actionButton: {
      transition: 'transform 0.1s ease-in-out',
      '&:hover': {
        transform: 'scale(1.05)',
      },
    },
  };

  return (
    <Container size="lg" p="md">
      <Title order={1} mb="md" style={{ color: '#f2f2f2' }}>
        Private Lessons for {claimedArtist.name}
      </Title>

      <Tabs
        styles={{
          tabLabel: {
            fontSize: '1rem',
            padding: '4px 12px',
            fontWeight: 500,
          },
          tab: {
            height: '44px',
            padding: '0 32px',
          },
          tabsListWrapper: {
            '@media (max-width: 768px)': {
              justifyContent: 'center',
            },
          },
        }}
        value={activeTab}
        onChange={setActiveTab}
      >
        <Tabs.List>
          <Tabs.Tab value="pl-enable">Enable Private Lessons</Tabs.Tab>
          <Tabs.Tab value="pl-types">Private Lesson Types</Tabs.Tab>
          <Tabs.Tab value="pl-pages">Private Lesson Pages</Tabs.Tab>
          <Tabs.Tab value="pl-bookings">Private Lesson Bookings</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="pl-enable" pt="xl">
          <Card shadow="sm" p="lg" style={{ backgroundColor: '#757575' }}>
            <Title order={5} mb="md" style={{ color: '#f2f2f2' }}>
              Private Lessons
            </Title>
            <Text mb="md">
              Manage your private lessons for {claimedArtist.name} here. You can view your upcoming lessons, cancel
              lessons, and view your lesson history.
            </Text>
            <Group>
              <Text>Enable Private Lesson Pages</Text>
              <Switch checked={lessonsEnabled} onChange={handleToggleLessons} size="md" disabled={isLoading} />
            </Group>
            <Box mt="md">
              <Text fw="600" c={lessonsEnabled ? 'green' : 'gray'}>
                Private lessons are currently {lessonsEnabled ? 'enabled' : 'disabled'}.
              </Text>
            </Box>
          </Card>
        </Tabs.Panel>

        <Tabs.Panel value="pl-types" pt="xl">
          <Button mb="xl" onClick={() => openDrawer('new-type')}>
            Create New Private Lesson Type
          </Button>

          <Title order={2} mb="md" style={{ color: '#f2f2f2' }}>
            Existing Private Lesson Types
          </Title>

          {isLoading ? (
            <Text>Loading lesson types...</Text>
          ) : lessonTypes.length > 0 ? (
            <Stack gap="sm">
              {lessonTypes.map((type) => (
                <Card key={type.id} shadow="sm" p="lg" style={{ backgroundColor: '#75757510' }}>
                  <Stack gap="0">
                    <Text>
                      <strong>Name:</strong> {type.name}
                    </Text>
                    <Text>
                      <strong>Location:</strong> {type.location}
                    </Text>
                    <Text>
                      <strong>Duration:</strong> {type.duration} minutes
                    </Text>
                    <Text>
                      <strong>Price:</strong> ${type.price}
                    </Text>
                    <Text>
                      <strong>Payment Required:</strong> {type.payment_required ? 'Yes' : 'No'}
                    </Text>
                  </Stack>

                  <Group mt="md">
                    <Button variant="filled" onClick={() => openDrawer('edit-type', type)}>
                      Edit Private Lesson Type
                    </Button>
                    <Button variant="filled" color="red">
                      Delete
                    </Button>
                  </Group>
                </Card>
              ))}
            </Stack>
          ) : (
            <Text>No private lesson types available. Create a new one above.</Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="pl-pages" pt="xl">
          <Button mb="xl" onClick={() => openDrawer('new-page')}>
            Create New Private Lesson Page
          </Button>

          <Title order={2} mb="md" style={{ color: '#f2f2f2' }}>
            Existing Private Lesson Pages
          </Title>

          {isLoading ? (
            <Text>Loading lesson pages...</Text>
          ) : lessonPages.length > 0 ? (
            <Stack gap="sm">
              {lessonPages.map((page) => (
                <Card key={page.id} shadow="sm" p="lg" style={{ backgroundColor: '#75757540' }}>
                  <Stack gap="0">
                    <Text>
                      <strong>Type:</strong> {page.private_lesson_type.name}
                    </Text>
                    <Text>
                      <strong>Description:</strong> {page.description}
                    </Text>
                    <Text>
                      <strong>Timezone:</strong> {page.timezone}
                    </Text>
                    <Text>
                      <strong>Created On:</strong> {formatDateTime(page.created_at)}
                    </Text>
                  </Stack>

                  <Group mt="lg">
                    <Button variant="outline">Edit Private Lesson Page</Button>
                    <Button
                      component={Link}
                      href={`/${claimedArtist.slug}/pl/${page.slug}`}
                      variant="filled"
                      size="md"
                      className="poppins"
                      style={{ marginLeft: 'auto' }}
                    >
                      View Private Lesson Page
                    </Button>
                  </Group>
                </Card>
              ))}
            </Stack>
          ) : (
            <Text>No private lesson pages available. Create a new one above.</Text>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="pl-bookings" pt="xl">
          <Title order={2} mb="md" style={{ color: '#f2f2f2' }}>
            Your Private Lesson Bookings
          </Title>

          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text size="lg" mb="md">
                Loading bookings...
              </Text>
              {/* You could add a spinner here */}
            </div>
          ) : lessonBookings.length > 0 ? (
            // Group bookings by status
            Object.entries(
              lessonBookings.reduce(
                (groups, booking) => {
                  const status = booking.status;
                  groups[status] = groups[status] || [];
                  groups[status].push(booking);
                  return groups;
                },
                {} as Record<number, PrivateLessonBooking[]>,
              ),
            ).map(([status, bookingsInGroup]) => (
              <Box key={status} mb="xl">
                <Group justify="apart" mb="md">
                  <Title order={3} style={{ color: '#f2f2f2' }}>
                    {getStatusText(Number(status))} Privates
                  </Title>
                  <Badge size="lg" color={getStatusColor(Number(status))}>
                    {bookingsInGroup.length} {bookingsInGroup.length === 1 ? 'Booking' : 'Bookings'}
                  </Badge>
                </Group>

                <div style={modernCardStyles.bookingsContainer}>
                  {/* Sort bookings by time slot start time */}
                  {bookingsInGroup
                    .sort(
                      (a, b) => new Date(a.time_slot.start_time).getTime() - new Date(b.time_slot.start_time).getTime(),
                    )
                    .map((booking) => (
                      <Paper key={booking.id} shadow="md" p={0} style={modernCardStyles.bookingCard}>
                        {/* Card Header with Time and Status */}
                        <div style={modernCardStyles.cardHeader}>
                          <Group justify="apart">
                            <Text size="lg" fw={600} c="#EA580C">
                              {formatDateTime(booking.time_slot.start_time)}
                            </Text>
                            <Badge size="lg" color={getStatusColor(booking.status)}>
                              {getStatusText(booking.status)}
                            </Badge>
                          </Group>
                        </div>

                        {/* Card Body with Details */}
                        <div style={modernCardStyles.cardBody}>
                          <div style={modernCardStyles.infoRow}>
                            <ThemeIcon color="gray" size="md" radius="xl" style={modernCardStyles.iconWrapper}>
                              <IconUser size={16} />
                            </ThemeIcon>
                            <Text style={{ color: '#f2f2f2' }}>
                              <strong>Student:</strong> {getStudentName(booking.id)}
                            </Text>
                          </div>

                          <div style={modernCardStyles.infoRow}>
                            <ThemeIcon color="gray" size="md" radius="xl" style={modernCardStyles.iconWrapper}>
                              <IconClock size={16} />
                            </ThemeIcon>
                            <Text style={{ color: '#f2f2f2' }}>
                              <strong>Time:</strong> {getUTCTime(booking.time_slot.start_time)} -{' '}
                              {getUTCTime(booking.time_slot.end_time)}
                            </Text>
                          </div>

                          <div style={modernCardStyles.infoRow}>
                            <ThemeIcon color="gray" size="md" radius="xl" style={modernCardStyles.iconWrapper}>
                              <IconTag size={16} />
                            </ThemeIcon>
                            <Text style={{ color: '#f2f2f2' }}>
                              <strong>Type:</strong> {getLessonTypeName(booking.id)}
                            </Text>
                          </div>

                          <div style={modernCardStyles.infoRow}>
                            <ThemeIcon color="gray" size="md" radius="xl" style={modernCardStyles.iconWrapper}>
                              <IconCoin size={16} />
                            </ThemeIcon>
                            <Text style={{ color: '#f2f2f2' }}>
                              <strong>Price:</strong> ${getLessonTypePrice(booking.id)}
                            </Text>
                          </div>

                          {booking.notes && (
                            <Text mt="md" size="sm" color="dimmed">
                              <strong>Notes:</strong> {booking.notes}
                            </Text>
                          )}
                        </div>

                        {/* Card Footer with Actions - Updated version with reschedule and approve buttons */}
                        <div style={modernCardStyles.cardFooter}>
                          <Group justify="apart">
                            <Button
                              component="a"
                              href={`/users/${currentUser.username}/private_lesson_pages/${getArtistSlug(booking.private_lesson_page_id)}/${getPageSlug(booking.private_lesson_page_id)}/private_lesson_bookings/${booking.id}`}
                              leftSection={<IconEye size={16} />}
                              variant="light"
                              color="orange"
                              style={modernCardStyles.actionButton}
                            >
                              View Details
                            </Button>

                            <Group gap="xs">
                              {booking.status === 0 && ( // Pending bookings - full action set
                                <>
                                  <Tooltip label="Approve Booking">
                                    <ActionIcon
                                      color="green"
                                      size="lg"
                                      onClick={() => openApproveModal(booking.id)}
                                      style={modernCardStyles.actionButton}
                                    >
                                      <IconCheck size={20} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label="Reschedule">
                                    <ActionIcon
                                      color="blue"
                                      size="lg"
                                      component="a"
                                      disabled
                                      href={`/users/${currentUser.username}/edit_booking/${booking.id}`}
                                      style={modernCardStyles.actionButton}
                                    >
                                      <IconCalendarTime size={20} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label="Decline Booking">
                                    <ActionIcon
                                      color="red"
                                      size="lg"
                                      onClick={() => openDeclineModal(booking.id)}
                                      style={modernCardStyles.actionButton}
                                    >
                                      <IconTrash size={20} />
                                    </ActionIcon>
                                  </Tooltip>
                                </>
                              )}

                              {booking.status === 1 && ( // Approved bookings - reschedule and mark complete
                                <>
                                  <Tooltip label="Mark as Completed">
                                    <ActionIcon
                                      color="green"
                                      size="lg"
                                      onClick={() => handleMarkCompleted(booking.id)}
                                      style={modernCardStyles.actionButton}
                                    >
                                      <IconCheck size={20} />
                                    </ActionIcon>
                                  </Tooltip>
                                  <Tooltip label="Reschedule">
                                    <ActionIcon
                                      color="blue"
                                      size="lg"
                                      component="a"
                                      disabled
                                      href={`/users/${currentUser.username}/edit_booking/${booking.id}`}
                                      style={modernCardStyles.actionButton}
                                    >
                                      <IconCalendarTime size={20} />
                                    </ActionIcon>
                                  </Tooltip>
                                </>
                              )}

                              {booking.status === 2 && ( // Completed bookings - just view
                                <Tooltip label="Add Recap">
                                  <ActionIcon
                                    color="violet"
                                    size="lg"
                                    onClick={() => openRecapModal(booking.id)}
                                    style={modernCardStyles.actionButton}
                                  >
                                    <IconNotes size={20} />
                                  </ActionIcon>
                                </Tooltip>
                              )}
                            </Group>
                          </Group>
                        </div>
                      </Paper>
                    ))}
                </div>
              </Box>
            ))
          ) : (
            <Paper
              p="sm"
              shadow="sm"
              style={{
                backgroundColor: '#757575',
                textAlign: 'center',
                borderRadius: '10px',
              }}
            >
              <ThemeIcon size={60} radius={100} color="gray" mx="auto" mb="md">
                <IconCalendar size={30} />
              </ThemeIcon>
              <Title order={3} mb="sm">
                No Bookings Found
              </Title>
              <Text color="dimmed">You don't have any private lesson bookings yet.</Text>
            </Paper>
          )}

          <Modal
            opened={isApproveModalOpen}
            onClose={() => setIsApproveModalOpen(false)}
            title="Confirm Approval"
            centered
            styles={{
              title: { fontWeight: 600 },
              content: { backgroundColor: '#757575', color: '#f2f2f2' },
              header: { backgroundColor: '#4CAF50', color: '#f2f2f2' },
            }}
          >
            <Text size="md" className="my-4 text-white">
              Are you sure you want to approve this lesson request?
            </Text>
            <Group justify="right">
              <Button variant="filled" color="gray" onClick={() => setIsApproveModalOpen(false)}>
                Cancel
              </Button>
              <Button color="green" onClick={handleApproveBooking} loading={isLoading}>
                Approve Lesson Request
              </Button>
            </Group>
          </Modal>
          <Modal
            opened={isDeclineModalOpen}
            onClose={() => setIsDeclineModalOpen(false)}
            title="Confirm Decline"
            centered
            styles={{
              title: { fontWeight: 600 },
              content: { backgroundColor: '#757575', color: '#f2f2f2' },
              header: { backgroundColor: '#cc0000', color: '#f2f2f2' },
            }}
          >
            <Text size="md" className="my-4 text-white">
              Are you sure you want to decline this lesson request? This action cannot be undone.
            </Text>
            <Group justify="right">
              <Button variant="filled" color="gray" onClick={() => setIsDeclineModalOpen(false)}>
                Cancel
              </Button>
              <Button color="red" onClick={handleDeclineBooking} loading={isLoading}>
                Decline Lesson Request
              </Button>
            </Group>
          </Modal>
          <Modal
            opened={isRecapModalOpen}
            onClose={() => setIsRecapModalOpen(false)}
            title="Add Lesson Recap"
            centered
            styles={{
              title: { fontWeight: 600 },
              modal: { backgroundColor: '#2c2c2c' },
            }}
          >
            <Text size="md" mb="md">
              Add a video recap or notes for this completed lesson.
            </Text>
            <TextInput
              label="Video URL or Notes"
              placeholder="e.g. https://youtu.be/example or your recap notes"
              value={recapText}
              onChange={(e) => setRecapText(e.target.value)}
              mb="lg"
            />
            <Group justify="right">
              <Button variant="subtle" onClick={() => setIsRecapModalOpen(false)}>
                Cancel
              </Button>
              <Button color="blue" onClick={handleSaveRecap} loading={isLoading}>
                Save Recap
              </Button>
            </Group>
          </Modal>
        </Tabs.Panel>
      </Tabs>

      {/* Dynamic drawer for forms */}
      <Drawer
        opened={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title={drawerTitle}
        position="right"
        size="lg"
        styles={{
          content: { backgroundColor: '#3d3c3e' },
          header: { backgroundColor: '#2c2b2d', marginBottom: '2rem' },
          title: { color: '#f2f2f2', fontWeight: 600, fontSize: '1.5rem' },
        }}
      >
        {renderDrawerContent()}
      </Drawer>
    </Container>
  );
};

export default PrivateLessonsManagement;
